/**
 * Schema Configuration Manager for Center Hub Site
 *
 * Provides access to Google Sheets schema configuration from Supabase.
 * Eliminates hardcoded column mappings and ensures schema consistency across frontend/backend.
 *
 * Author: Data Architecture Alignment Team
 * Created: November 2025
 */

import { createClient } from '@supabase/supabase-js';

// ============================================================================
// SCHEMA MANAGER CLASS
// ============================================================================

export class SchemaConfigManager {
  constructor(supabaseUrl, supabaseKey) {
    this.supabase = createClient(supabaseUrl, supabaseKey);
    this.cache = null;
    this.cacheTimestamp = null;
    this.cacheTTL = 3600000; // 1 hour in milliseconds
  }

  /**
   * Check if cached schema is still valid
   */
  isCacheValid() {
    if (!this.cache || !this.cacheTimestamp) {
      return false;
    }

    const now = Date.now();
    const age = now - this.cacheTimestamp;
    return age < this.cacheTTL;
  }

  /**
   * Fetch active schema from Supabase
   */
  async fetchActiveSchema(forceRefresh = false) {
    // Return cached if valid
    if (!forceRefresh && this.isCacheValid() && this.cache) {
      console.debug('[SchemaConfig] Using cached schema');
      return this.cache;
    }

    console.info('[SchemaConfig] Fetching active schema from Supabase...');

    try {
      const { data, error } = await this.supabase
        .from('schema_configs')
        .select('*')
        .eq('is_active', true)
        .eq('is_draft', false)
        .single();

      if (error) {
        throw new Error(`Failed to fetch schema: ${error.message}`);
      }

      if (!data) {
        throw new Error('No active schema found in schema_configs table');
      }

      // Validate schema structure
      if (!data.version || !data.config || !data.config.columns) {
        throw new Error('Invalid schema structure: missing required fields');
      }

      console.info(`[SchemaConfig] Loaded schema version: ${data.version}`);

      // Update cache
      this.cache = data;
      this.cacheTimestamp = Date.now();

      return this.cache;
    } catch (error) {
      console.error('[SchemaConfig] Error fetching schema:', error);
      throw error;
    }
  }

  /**
   * Get column mapping (field → column letter)
   */
  async getColumnMapping(forceRefresh = false) {
    const schema = await this.fetchActiveSchema(forceRefresh);

    const mapping = {};
    schema.config.columns.forEach((col) => {
      if (col.field_name && col.column_letter) {
        mapping[col.field_name] = col.column_letter;
      }
    });

    return mapping;
  }

  /**
   * Get reverse column mapping (column letter → field)
   */
  async getReverseMapping(forceRefresh = false) {
    const mapping = await this.getColumnMapping(forceRefresh);

    const reverse = {};
    Object.entries(mapping).forEach(([field, column]) => {
      reverse[column] = field;
    });

    return reverse;
  }

  /**
   * Get column letter for a specific field
   */
  async getColumnLetter(fieldName, forceRefresh = false) {
    const mapping = await this.getColumnMapping(forceRefresh);
    return mapping[fieldName] || null;
  }

  /**
   * Get field name for a specific column letter
   */
  async getFieldName(columnLetter, forceRefresh = false) {
    const mapping = await this.getReverseMapping(forceRefresh);
    return mapping[columnLetter] || null;
  }

  /**
   * Get all field names in schema
   */
  async getAllFields(forceRefresh = false) {
    const schema = await this.fetchActiveSchema(forceRefresh);
    return schema.config.columns
      .map((col) => col.field_name)
      .filter((field) => !!field);
  }

  /**
   * Get schema settings (sheetName, totalColumns, etc.)
   */
  async getSettings(forceRefresh = false) {
    const schema = await this.fetchActiveSchema(forceRefresh);
    return schema.config.settings || {};
  }

  /**
   * Get the dynamic sheet range based on schema settings
   * @returns {string} Range like "Jobs!A1:BB"
   */
  async getSheetRange(forceRefresh = false) {
    const settings = await this.getSettings(forceRefresh);
    const sheetName = settings.sheetName || 'Sheet1';
    const totalColumns = settings.totalColumns || 52;

    // Convert column count to letter (e.g., 52 -> "AZ", 54 -> "BB")
    const colLetter = this.numberToColumnLetter(totalColumns);

    return `${sheetName}!A1:${colLetter}`;
  }

  /**
   * Convert column number to letter (1=A, 26=Z, 27=AA, 52=AZ, 54=BB)
   */
  numberToColumnLetter(num) {
    let letter = '';
    while (num > 0) {
      const remainder = (num - 1) % 26;
      letter = String.fromCharCode(65 + remainder) + letter;
      num = Math.floor((num - 1) / 26);
    }
    return letter;
  }

  /**
   * Get column definition for a field
   */
  async getColumnDefinition(fieldName, forceRefresh = false) {
    const schema = await this.fetchActiveSchema(forceRefresh);
    return schema.config.columns.find((col) => col.field_name === fieldName) || null;
  }

  /**
   * Validate schema version matches expected
   */
  async validateVersion(expectedVersion, forceRefresh = false) {
    const schema = await this.fetchActiveSchema(forceRefresh);

    if (schema.version !== expectedVersion) {
      throw new Error(
        `Schema version mismatch! Expected: ${expectedVersion}, Actual: ${schema.version}`
      );
    }

    return true;
  }

  /**
   * Clear cache (force fresh fetch on next access)
   */
  clearCache() {
    this.cache = null;
    this.cacheTimestamp = null;
    console.info('[SchemaConfig] Cache cleared');
  }

  /**
   * Get cache info
   */
  getCacheInfo() {
    if (!this.cache || !this.cacheTimestamp) {
      return { cached: false, version: null, age: null };
    }

    const age = Date.now() - this.cacheTimestamp;

    return {
      cached: true,
      version: this.cache.version,
      age: Math.floor(age / 1000), // seconds
    };
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

let globalSchemaManager = null;

/**
 * Get global schema manager singleton
 *
 * Usage:
 * ```js
 * const manager = getSchemaManager();
 * const mapping = await manager.getColumnMapping();
 * ```
 */
export function getSchemaManager() {
  if (!globalSchemaManager) {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL;
    const supabaseKey =
      process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseKey) {
      throw new Error('Supabase credentials not configured in environment variables');
    }

    globalSchemaManager = new SchemaConfigManager(supabaseUrl, supabaseKey);
  }

  return globalSchemaManager;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Quick helper to get column letter for a field
 *
 * Usage:
 * ```js
 * const statusCol = await getColumnForField('status'); // "C"
 * ```
 */
export async function getColumnForField(fieldName) {
  const manager = getSchemaManager();
  return await manager.getColumnLetter(fieldName);
}

/**
 * Quick helper to get field name for a column
 *
 * Usage:
 * ```js
 * const fieldName = await getFieldForColumn('C'); // "status"
 * ```
 */
export async function getFieldForColumn(columnLetter) {
  const manager = getSchemaManager();
  return await manager.getFieldName(columnLetter);
}

/**
 * Get full column mapping
 *
 * Usage:
 * ```js
 * const mapping = await getFullMapping();
 * console.log(mapping); // { job_id: "A", status: "C", ... }
 * ```
 */
export async function getFullMapping() {
  const manager = getSchemaManager();
  return await manager.getColumnMapping();
}
